import { Autocomplete, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, TextField } from "@mui/material";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, isArray } from "lodash";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../../../redux/slices/treeSlice";
import LabeledDropDown from "../../../TemplateProperties/TPDropDown";
import { MoreHorizOutlined } from "@mui/icons-material";
import TypableDropdownInput from "../../../../commonComponents/TypableDropdownInput";
import WorkflowSessionModal from "../../WorkflowSessionModal";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import { validateWorkflowItemName } from "../../../../utils/utilFunctions";
import ErrorModal from "../../Forms/FieldDataValidation/ErrorModal";

export default function CallWorkflowProperties() {
  //default vars
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree, leftTree } = useSelector(state => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree.body.selectedNode;
  const { translatedData } = rightTree.tree.body;
  const { account } = useSelector(state => state.queryParams);


  //main save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      if (bulk) {
        bulk.forEach((item) => {
          if (item.value === null) {
            if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
          } else {
            obj[item.key] = item.value;
          }
        });
      } else {
        if (key === "NAME") {
          obj.title = value;
          obj[key] = value;
        } else {
          // Update other properties directly on the object.
          if (value === null) {
            if (obj?.hasOwnProperty(key)) delete obj[key];
          } else {
            obj[key] = value;
          }
        }
      }

      // Dispatch an action to update the selected node in the state.
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    // Dispatch an action to update the translated tree data in the state.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);


  //*** "Name" - NAME - text field ***//
  const defaultName = selectedNode?.NAME || "";
  const [name, setName] = useState(defaultName);

  //ID string, displayed on the NAME field
  const wfItemId = selectedNode?.ID;

  // Handle changes to the data name, specific logic for onChange event
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
    }
  }

  // Handle changes to the data name, specific logic for onBlur event
  const handleNameBlur = (eVal, e) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      eVal, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'CALL_WORKFLOW'
    );
    
    if (validationResult.isValid) {
      const newName = eVal?.trim() || '';
      setName(newName);
      if (newName !== oldName) {
        // Mark template as changed immediately to ensure flag is set before tab switch
        dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
        submitChange('NAME', newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 200);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setNameErrorMessage(t(validationResult.errorMessage));
        setNameErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setName(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  }

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
    }
  };

  //*** "Do not Log Workflow Item Details" - LOG_WFITEM_DETAILS - checkbox ***//
  const defaultLogWorkflowChecked = selectedNode?.LOG_WFITEM_DETAILS === "FALSE" ? true : false;
  const [logWorkflowChecked, setLogWorkflowChecked] = useState(defaultLogWorkflowChecked);

  const handleLogWorkflowChange = (e) => {
    const checked = e?.target?.checked;
    setLogWorkflowChecked(checked);
    debounceChangeHandler('LOG_WFITEM_DETAILS', checked ? 'FALSE' : 'TRUE');
  }

  //*** "Run Concurrently" - CONCURRENT - checkbox - ***//
  const defaultConcurrentChecked = selectedNode?.CONCURRENT === "FALSE" ? false : true;
  const [concurrentChecked, setConcurrentChecked] = useState(defaultConcurrentChecked);

  const handleConcurrentChange = (e) => {
    const checked = e?.target?.checked;
    setConcurrentChecked(checked);
    if (checked) {
      debounceChangeHandler('CONCURRENT', 'TRUE');
    } else {
      debounceChangeHandler(null, null, [
        {key: 'CONCURRENT', value: "FALSE"},
        {key: 'DISTRIBUTE_CALL', value: "FALSE"},
      ]);
      setDistributeCallChecked(false);
    }
  }

  //*** "Run Distributed on Cluster" - DISTRIBUTE_CALL - checkbox ***//
  const defaultDistributeCallChecked = selectedNode?.DISTRIBUTE_CALL === "TRUE" ? true : false;
  const [distributeCallChecked, setDistributeCallChecked] = useState(defaultDistributeCallChecked);

  const handleDistributeCallChange = (e) => {
    const checked = e?.target?.checked;
    setDistributeCallChecked(checked);
    debounceChangeHandler('DISTRIBUTE_CALL', checked ? 'TRUE' : 'FALSE');
  }

  //*** "Disable Workflow Item" - ENABLED - checkbox ***//
  const defaultEnabledChecked = selectedNode?.ENABLED === "TRUE" ? false : true;
  const [enabledChecked, setEnabledChecked] = useState(defaultEnabledChecked);

  const handleEnabledChange = (e) => {
    const checked = e?.target?.checked;
    setEnabledChecked(checked);
    debounceChangeHandler('ENABLED', checked ? 'FALSE' : 'TRUE');
  }

  //*** Ignore Call Workflow Errors - CWF_IGNORE_ERRORS - checkbox ***//
  const defaultIgnoreErrorsChecked = selectedNode?.CWF_IGNORE_ERRORS === "TRUE" ? true : false;
  const [ignoreErrorsChecked, setIgnoreErrorsChecked] = useState(defaultIgnoreErrorsChecked);

  const handleIgnoreErrorsChange = (e) => {
    const checked = e?.target?.checked;
    setIgnoreErrorsChecked(checked);
    debounceChangeHandler('CWF_IGNORE_ERRORS', checked ? 'TRUE' : 'FALSE');
  }

  const [wfGroupOpen, setWfGroupOpen] = useState({ open: false, isFirstTime: false });
  const [wfGroupForcedValue, setWfGroupForcedValue] = useState(null);
  
  const [wfNameOpen, setWfNameOpen] = useState({ open: false, isFirstTime: false });
  const [wfNameForcedValue, setWfNameForcedValue] = useState(null);
  
  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');


  //take a string, and filter out values that are inbetween % symbols and return the string that is not enclosed in % symbols
  //example %ACCOUNT_DATA,Test%%ACCOUNT_NAME%sample1%SYSTEM_DATA_TOMORROW% should return sample1
  const getValueFromString = (str) => {
    const regex = /%([^%]+)%/g;
    const matches = str.match(regex);
    //if there are any matches, remove the matches from the string and return the string that is not enclosed in % symbols
    if (matches) {
      const filteredString = str.replace(regex, '').trim();
      return filteredString;
    }
    //if there are no matches, return the original string
    return str;
  }

  const filteredWfGroup = getValueFromString(selectedNode?.WF_GROUP || '');
  const filteredWfName = getValueFromString(selectedNode?.WF_NAME || '');

  let workflowGroupOptions = [
    t('SELECT'),
  ];

  if (leftTree.tree.body?.translatedData?.length > 0) {
    leftTree.tree.body?.translatedData.map((item) => item.title).forEach((wfg) => {
      workflowGroupOptions.push(wfg);
    })
  }

  const handleWfGroupChange = (val) => {
    if (val || val === '') {
      if (val === t('SELECT') || val === '') {
        debounceChangeHandler('WF_GROUP', null);
      } else {
        debounceChangeHandler('WF_GROUP', val);
      }
    }
  }

  //*** "Workflow to Call" - WF_NAME - dropdown ***//
  let workflowNameOptions = [
    t('SELECT'),
    // ...leftTree.tree.body?.translatedData?.filter((item) => item.title === filteredWfGroup)?.[0]?.children?.map((wfname) => wfname.title)
  ];

  if (
    leftTree.tree.body?.translatedData?.length > 0 && 
    leftTree.tree.body?.translatedData?.map((item) => item.title).includes(filteredWfGroup) &&
    leftTree.tree.body?.translatedData?.filter((item) => item.title === filteredWfGroup)?.[0]?.children?.length > 0
  ) {
    // if wf group is found in left tree, get the children of the wf group and add them to the options of wf name dropdown
    leftTree.tree.body?.translatedData.filter((item) => item.title === filteredWfGroup)[0].children.forEach((wfname) => {
      if (wfname?.type === "workflow") {
        workflowNameOptions.push(wfname.title);
      }
    })
  }    

  const handleWfNameChange = (val) => {
    if (val || val === '') {
      if (val === t('SELECT') || val === '') {
        debounceChangeHandler('WF_NAME', null);
      } else {
        debounceChangeHandler('WF_NAME', val);
      }
    }
  }

  //check if the workflow group is found in the left tree
  const workflowGroupFound = leftTree.tree.body?.translatedData.map((item) => item.title).includes(filteredWfGroup) ? true : false;

  //check if the workflow is viewable, meaning it has a group and a name and the group and name are in the left tree
  const workflowFound = 
    selectedNode?.WF_GROUP && 
    selectedNode?.WF_NAME && 
    leftTree.tree.body?.translatedData.map((item) => item.title).includes(filteredWfGroup) && 
    leftTree.tree.body?.translatedData.filter((item) => item.title === filteredWfGroup)[0]?.children?.length > 0 && 
    leftTree.tree.body?.translatedData.filter((item) => item.title === filteredWfGroup)[0]?.children.map((item) => item.title).includes(filteredWfName) && 
    leftTree.tree.body?.translatedData.filter((item) => item.title === filteredWfGroup)[0]?.children.filter((item) => item.title === filteredWfName)[0]?.type === "workflow"
  ? true : false;

  const handleViewWorkflowClick = () => {
    // Logic to view the workflow
    if (!selectedNode?.WF_GROUP || !selectedNode?.WF_NAME) return;
    if (!workflowFound) return;
    dispatch(treeActions.setForceTemplate({
      workflowGroup: filteredWfGroup,
      template: filteredWfName,
      screen: "workflow"
    }));
  }

  return (
    <div>
      {/* "Name" text field + ID number */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel
          htmlFor='translated-data-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('COL_NAME')}
        </InputLabel>
        <OutlinedInput
          id="translated-data-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          endAdornment={<div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>}
          sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          onBlur={(e) => handleNameBlur(e?.target?.value, e)}
          onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
        />
      </FormControl>

      {/* "Do not Log Workflow Item Details" checkbox */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <FormControlLabel
          control={
            <Checkbox
              checked={logWorkflowChecked}
              onChange={handleLogWorkflowChange}
              name='logWorkflowChecked'
              color='primary'
            />
          }
          label={t('LBL_LOG_NO_WFITEM_DETAILS')}
        />
      </FormControl>

      {/* "Workflow Group" and "Workflow to Call dropdowns" */}
      <div>
        <div style={{ marginTop: "10px", display: "flex", gap: "5px" }}>

          <TypableDropdownInput 
            label={t("WORKFLOW_GROUP_MENU")}
            options={workflowGroupOptions}
            value={selectedNode?.WF_GROUP || t('SELECT')}
            valueChangeHandler={handleWfGroupChange}
            forcedValue={wfGroupForcedValue}
          />
          <Button 
            size='small' 
            // disabled={disableControl || false} 
            variant='outlined' 
            onClick={() => setWfGroupOpen({open: true, isFirstTime: true})} 
            sx={{width: '40px', minWidth:'40px'}}
          >
              <MoreHorizOutlined fontSize='small' />
          </Button>
        </div>
        {(!workflowGroupFound && selectedNode?.WF_GROUP && selectedNode?.WF_GROUP !== t('SELECT')) && (
          <div style={{fontSize: "11px", color: "#666", fontStyle: "italic", paddingBottom: "5px"}}>
            {t("LBL_WF_GROUP_MISSING")}
          </div>
        )}
        
        <div style={{ marginTop: "10px", display: "flex", gap: "5px" }}>
          <TypableDropdownInput 
            label={t("LBL_WORKFLOW_TO_CALL").replace(":", "")}
            options={workflowNameOptions}
            value={selectedNode?.WF_NAME || t('SELECT')}
            valueChangeHandler={handleWfNameChange}
            forcedValue={wfNameForcedValue}
          />
          {/* <LabeledDropDown
            label={t("LBL_WORKFLOW_TO_CALL").replace(":", "")}
            options={workflowNameOptions}
            value={wfNameValue}
            onChange={handleWfNameChange}
          /> */}
          <Button 
            size='small' 
            // disabled={disableControl || false} 
            variant='outlined' 
            onClick={() => setWfNameOpen({open: true, isFirstTime: true})} 
            sx={{width: '40px', minWidth:'40px',}}
          >
              <MoreHorizOutlined fontSize='small' />
          </Button>
          {workflowFound ? (
            <Button 
              size='small' 
              // disabled={disableControl || false} 
              variant='outlined' 
              onClick={handleViewWorkflowClick} 
              sx={{width: '40px', minWidth:'40px'}}
            >
              {t("MENU_VIEW")}
            </Button>
          ) : (
            <LightTooltip
              title={t("WORKFLOW_TEMPLATE_NOT_FOUND")}
              arrow
              placement="right"
            >
              <div style={{ height: "100%" }}>
                <Button
                  size='small'
                  disabled={true}
                  variant='outlined'
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "100%"
                  }}
                  // onClick={handleViewWorkflowClick}
                >
                  {t("MENU_VIEW")}
                </Button>
              </div>
            </LightTooltip>
          )}
        </div>
        {(workflowGroupFound && !workflowFound && selectedNode?.WF_NAME && selectedNode?.WF_NAME !== t('SELECT')) && (
          <div style={{fontSize: "11px", color: "#666", fontStyle: "italic", paddingBottom: "5px"}}>
            {t("LBL_WORKFLOW_TO_CALL_MISSING")}
          </div>
        )}
      </div>


      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {/* "Run Concurrently" checkbox*/}
        <FormControl variant='outlined' style={{ minWidth: "153px" }} size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={concurrentChecked}
                onChange={handleConcurrentChange}
                name='concurrentChecked'
                color='primary'
              />
            }
            label={t('LBL_RUN_CONCURRENTLY')}
          />
        </FormControl>

        {/* "Run Distributed on Cluster" checkbox - enabled if node has CONCURRENT="TRUE*/}
        {selectedNode?.CONCURRENT === "TRUE" && (
          <FormControl variant='outlined' style={{ minWidth: "153px" }} size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={distributeCallChecked}
                  onChange={handleDistributeCallChange}
                  name='distributeCallChecked'
                  color='primary'
                />
              }
              label={t('LBL_RUN_DISTRIBUTED')}
            />
          </FormControl>
        )}
        

        {/* "Disable Workflow Item" checkbox */}
        <FormControl variant='outlined' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledChecked}
                onChange={handleEnabledChange}
                name='enabledChecked'
                color='primary'
              />
            }
            label={t('LBL_DISABLE_WFITEM')}
          />
        </FormControl>

        {/* "Ignore Call Workflow Errors" checkbox */}
        <FormControl variant='outlined' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={ignoreErrorsChecked}
                onChange={handleIgnoreErrorsChange}
                name='ignoreErrorsChecked'
                color='primary'
              />
            }
            label={t('LBL_IGNORE_CWF_ERRORS')}
          />
        </FormControl>
      </div>

      {/* "Workflow Group" modal triggered by adjacent button */}
      <WorkflowSessionModal
        valueLabel={t("WORKFLOW_GROUP_MENU")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={wfGroupOpen}
        setModalOpen={setWfGroupOpen}
        handleClose={() => setWfGroupOpen({ open: false, isFirstTime: false })}
        initialValue={selectedNode?.WF_GROUP || ""}
        valuesHandler={(value) => setWfGroupForcedValue(value)}
      />

      {/* "Workflow To Call" modal triggered by adjacent button */}
      <WorkflowSessionModal
        valueLabel={t("LBL_WORKFLOW_TO_CALL").replace(":", "")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={wfNameOpen}
        setModalOpen={setWfNameOpen}
        handleClose={() => setWfNameOpen({ open: false, isFirstTime: false })}
        initialValue={selectedNode?.WF_NAME || ""}
        valuesHandler={(value) => setWfNameForcedValue(value)}
      />

      {/* Error Modal for name validation */}
      <ErrorModal
        errorModal={nameErrorModal}
        setErrorModal={setNameErrorModal}
        errorMessage={nameErrorMessage}
        errorType={t('ERROR')}
        handleErrorModal={handleNameErrorModal}
      />
    </div>
  );
}

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};